<template>
  <v-card outlined class="mb-3 delivery-card">
    <div class="d-flex flex-wrap align-center justify-space-between px-5 py-4 delivery-card__header">
      <div class="d-flex align-center">
        <v-avatar size="36" :color="isComplete && !editing ? 'green lighten-5' : 'blue lighten-5'" class="mr-3">
          <v-icon small :color="isComplete && !editing ? 'green darken-2' : 'blue darken-2'">
            {{ isComplete && !editing ? "mdi-check" : "mdi-truck-outline" }}
          </v-icon>
        </v-avatar>
        <div>
          <h3 class="text-subtitle-1 font-weight-medium my-0">Datos de despacho</h3>
          <span v-if="isComplete && !editing" class="text-caption green--text text--darken-2">
            Completos{{ savedAtLabel ? ` · guardados ${savedAtLabel}` : "" }}
          </span>
          <span v-else class="text-caption grey--text text--darken-2">
            Obligatorios para enviar la cubicación
          </span>
        </div>
      </div>
      <div class="d-flex flex-wrap align-center" style="gap: 8px">
        <v-chip v-if="editing && missingCount" small color="orange lighten-5" text-color="orange darken-4">
          {{ missingCount === 1 ? "Falta 1 dato obligatorio" : `Faltan ${missingCount} datos obligatorios` }}
        </v-chip>
        <v-btn
          text
          small
          color="blue darken-2"
          :href="$router.resolve({ path: '/my-request-materials', query: { tab: 'cubicador' } }).href"
        >
          Ver mis solicitudes
        </v-btn>
        <v-btn
          v-if="!editing"
          small
          outlined
          color="blue darken-2"
          :disabled="disabled"
          @click="editing = true"
        >
          Editar
        </v-btn>
      </div>
    </div>

    <!-- Saved: read-only summary -->
    <v-row v-if="!editing" no-gutters class="px-5 py-4">
      <v-col cols="12" md="5" class="pr-4 mb-3 mb-md-0">
        <div class="summary-key">Dirección</div>
        <div class="summary-value">{{ saved.address }}</div>
        <div class="text-body-2 grey--text text--darken-3">{{ saved.commune }} · {{ saved.region }}</div>
      </v-col>
      <v-col cols="6" md="2" class="pr-4">
        <div class="summary-key">Contacto</div>
        <div class="summary-value">{{ saved.contact || "-" }}</div>
      </v-col>
      <v-col cols="6" md="2" class="pr-4">
        <div class="summary-key">Despacho</div>
        <div class="summary-value font-weight-medium">{{ formatLongDate(saved.desired_dispatch_date) }}</div>
      </v-col>
      <v-col cols="12" md="3" class="mt-3 mt-md-0">
        <div class="summary-key">Picking</div>
        <div class="summary-value">{{ pickingLabel(saved.desired_dispatch_date) }}</div>
      </v-col>
    </v-row>

    <!-- Editing -->
    <template v-else>
      <v-row class="px-5 pt-5 pb-1">
        <v-col cols="12" md="6" class="py-1">
          <label class="field-label" for="delivery-address">Dirección de despacho *</label>
          <v-text-field
            id="delivery-address"
            v-model="form.address"
            placeholder="Calle, número, oficina / galpón"
            :disabled="disabled"
            outlined
            dense
            hide-details="auto"
            :error="showErrors && !form.address"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="3" class="py-1">
          <label class="field-label" for="delivery-region">Región *</label>
          <v-autocomplete
            id="delivery-region"
            v-model="form.region"
            :items="regionNames"
            placeholder="Selecciona una región"
            :disabled="disabled"
            outlined
            dense
            hide-details="auto"
            no-data-text="Sin resultados"
            :error="showErrors && !form.region"
            @change="onRegionChange"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" sm="6" md="3" class="py-1">
          <label class="field-label" for="delivery-commune">Comuna *</label>
          <v-autocomplete
            id="delivery-commune"
            v-model="form.commune"
            :items="communeItems"
            item-text="name"
            item-value="name"
            placeholder="Escribe para buscar"
            :disabled="disabled"
            outlined
            dense
            hide-details="auto"
            no-data-text="Sin resultados"
            :error="showErrors && !form.commune"
            @change="onCommuneChange"
          >
            <template v-slot:item="{ item }">
              <div class="d-flex justify-space-between" style="width: 100%; gap: 8px">
                <span>{{ item.name }}</span>
                <span v-if="!form.region" class="text-caption grey--text">{{ item.region }}</span>
              </div>
            </template>
          </v-autocomplete>
        </v-col>
        <v-col cols="12" sm="6" md="3" class="py-1">
          <label class="field-label" for="delivery-date">Fecha de despacho deseada *</label>
          <v-text-field
            id="delivery-date"
            v-model="form.desired_dispatch_date"
            type="date"
            :min="minDate"
            :disabled="disabled"
            outlined
            dense
            :hint="`Desde el ${formatLongDate(minDate)} (2 días hábiles)`"
            persistent-hint
            :error="showErrors && !form.desired_dispatch_date"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="3" class="py-1">
          <label class="field-label" for="delivery-contact">Contacto en obra</label>
          <v-text-field
            id="delivery-contact"
            v-model="form.contact"
            placeholder="+56 9 …"
            :disabled="disabled"
            outlined
            dense
            hint="Opcional"
            persistent-hint
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="6" class="py-1">
          <span class="field-label">Picking</span>
          <div class="picking-box">{{ pickingLabel(form.desired_dispatch_date) }}</div>
        </v-col>
      </v-row>
      <div class="d-flex flex-wrap align-center justify-space-between px-5 py-3 mt-3 delivery-card__footer">
        <span class="text-caption grey--text text--darken-2">
          {{ prefilledFromProject ? "Dirección precargada desde el proyecto. Revisa y completa lo que falta." : "" }}
        </span>
        <div class="d-flex" style="gap: 8px">
          <v-btn v-if="isComplete" outlined small :disabled="saving" @click="discard">
            Descartar cambios
          </v-btn>
          <v-btn
            color="blue darken-2"
            class="text-white"
            small
            :loading="saving"
            :disabled="disabled"
            @click="save"
          >
            Guardar datos
          </v-btn>
        </div>
      </div>
    </template>
  </v-card>
</template>

<script>
import { mapActions } from "vuex";
import { minDispatchIsoDate } from "@/helpers/cubage";
import { CHILE_REGIONS } from "@/constants/chileRegions";

const EMPTY = { address: "", commune: "", region: "", contact: "", desired_dispatch_date: null };
const REQUIRED = ["address", "commune", "region", "desired_dispatch_date"];
const WEEKDAYS = ["dom", "lun", "mar", "mié", "jue", "vie", "sáb"];

// Accent/case-insensitive key, to match free-text project data to the official names
const normalize = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim()
    .replace(/^region (de |del )?/, "");

const COMMUNES = CHILE_REGIONS.flatMap((region) =>
  region.comunas.map((name) => ({ name, region: region.name }))
);

const findRegion = (value) => {
  const key = normalize(value);
  if (!key) return "";
  const region = CHILE_REGIONS.find(
    (item) => normalize(item.name) === key || normalize(item.name).startsWith(key) || key.startsWith(normalize(item.name))
  );
  return region ? region.name : "";
};

const findCommune = (value) => {
  const key = normalize(value);
  return COMMUNES.find((item) => normalize(item.name) === key) || null;
};

// v2 cubicación header (ms-cubicador /document_delivery_info): where and when to dispatch.
// Shown as a form until it's complete and saved, then as a read-only summary with "Editar".
// Región and comuna come from the official INE list; an empty header is pre-filled from the
// project's data (projectDefaults) when it matches that list.
export default {
  props: {
    documentId: {
      type: Number,
      default: null,
    },
    // { direccion, comuna, region } from the project detail
    projectDefaults: {
      type: Object,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: { ...EMPTY },
      saved: { ...EMPTY },
      editing: true,
      saving: false,
      showErrors: false,
      savedAt: null,
      prefilledFromProject: false,
      regionNames: CHILE_REGIONS.map((region) => region.name),
    };
  },
  computed: {
    minDate() {
      return minDispatchIsoDate();
    },
    communeItems() {
      return this.form.region ? COMMUNES.filter((item) => item.region === this.form.region) : COMMUNES;
    },
    missingCount() {
      return REQUIRED.filter((key) => !this.form[key]).length;
    },
    isComplete() {
      return REQUIRED.every((key) => this.saved[key]);
    },
    savedAtLabel() {
      return this.savedAt
        ? `hoy ${this.savedAt.toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" })}`
        : "";
    },
  },
  methods: {
    ...mapActions({
      getDeliveryInfo: "cubage/getDeliveryInfo",
      saveDeliveryInfo: "cubage/saveDeliveryInfo",
    }),
    parseDate(iso) {
      const [year, month, day] = iso.split("-").map(Number);
      return new Date(year, month - 1, day);
    },
    formatLongDate(iso) {
      if (!iso) return "-";
      const date = this.parseDate(iso);
      return `${WEEKDAYS[date.getDay()]} ${iso.split("-").reverse().join("/")}`;
    },
    // Picking starts 2 business days before dispatch (holidays are counted in ms-entrega,
    // so this preview only skips weekends)
    pickingLabel(iso) {
      if (!iso) return "Elige la fecha de despacho";
      const day = this.parseDate(iso);
      let counted = 0;
      while (counted < 2) {
        day.setDate(day.getDate() - 1);
        if (day.getDay() !== 0 && day.getDay() !== 6) counted += 1;
      }
      const pad = (value) => String(value).padStart(2, "0");
      const label = `${WEEKDAYS[day.getDay()]} ${pad(day.getDate())}/${pad(day.getMonth() + 1)}`;
      return `Se prepara desde el ${label} · editable hasta entonces`;
    },
    onRegionChange(region) {
      if (this.form.commune) {
        const commune = findCommune(this.form.commune);
        if (!commune || commune.region !== region) this.form.commune = "";
      }
    },
    onCommuneChange(name) {
      const commune = findCommune(name);
      if (commune) this.form.region = commune.region;
    },
    async load() {
      if (!this.documentId) return;
      const info = (await this.getDeliveryInfo({ document_id: this.documentId })) || {};
      this.saved = { ...EMPTY, ...info };
      const defaults = this.projectDefaults || {};
      const projectCommune = findCommune(defaults.comuna);
      this.prefilledFromProject = !this.saved.address && Boolean(defaults.direccion);
      this.form = {
        ...this.saved,
        address: this.saved.address || defaults.direccion || "",
        commune: this.saved.commune || (projectCommune ? projectCommune.name : ""),
        region:
          this.saved.region ||
          (projectCommune ? projectCommune.region : findRegion(defaults.region)),
      };
      this.editing = !this.isComplete;
      this.$emit("change", this.saved);
    },
    discard() {
      this.form = { ...this.saved };
      this.showErrors = false;
      this.editing = false;
    },
    async save() {
      if (this.missingCount) {
        this.showErrors = true;
        return;
      }
      this.saving = true;
      const resp = await this.saveDeliveryInfo({ document_id: this.documentId, ...this.form });
      this.saving = false;
      if (resp.error) {
        return this.$swal({ icon: "error", title: "Error", text: resp.error });
      }
      this.saved = { ...EMPTY, ...resp };
      this.savedAt = new Date();
      this.showErrors = false;
      this.editing = false;
      this.$emit("change", this.saved);
    },
  },
  watch: {
    documentId: {
      immediate: true,
      handler() {
        this.load();
      },
    },
  },
};
</script>

<style scoped>
.delivery-card {
  border-radius: 10px !important;
}
.delivery-card__header {
  border-bottom: 1px solid #eeeeee;
}
.delivery-card__footer {
  border-top: 1px solid #eeeeee;
  background: #fafafa;
  border-radius: 0 0 10px 10px;
}
.field-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #424242;
  margin-bottom: 6px;
}
.picking-box {
  min-height: 40px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  border-radius: 6px;
  background: #f5f7fa;
  color: #37474f;
  font-size: 14px;
}
.summary-key {
  font-size: 12px;
  font-weight: 500;
  color: #616161;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
}
.summary-value {
  font-size: 15px;
  color: #212121;
}
</style>
